import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import {
  Boxes, Wrench, PackageOpen, ShieldAlert, FileClock, GitPullRequestArrow,
  IndianRupee, Building2, ArrowRight, Store,
} from "lucide-react";
import camApi, { STATUS_META } from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Dashboard() {
  const { catName, tree } = useCam();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    camApi.dashboard()
      .then((r) => setD(r.dashboard))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const buildingName = (id) => tree.buildings.find((b) => String(b.id) === String(id))?.name || `Building ${id}`;
  const deptName = (id) => tree.departments.find((x) => String(x.id) === String(id))?.name || `Dept ${id}`;

  const statusData = useMemo(() => Object.entries(d?.byStatus || {}).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v, color: STATUS_META[k]?.color || "#64748b" })), [d]);
  const catData = useMemo(() => Object.entries(d?.byCategory || {}).map(([k, v]) => ({ name: catName(Number(k)), value: v })).sort((a, b) => b.value - a.value).slice(0, 8), [d, catName]);
  const buildingData = useMemo(() => Object.entries(d?.byBuilding || {}).map(([k, v]) => ({ name: buildingName(k), value: v })).sort((a, b) => b.value - a.value).slice(0, 8), [d]); // eslint-disable-line react-hooks/exhaustive-deps
  const deptData = useMemo(() => Object.entries(d?.byDept || {}).map(([k, v]) => ({ id: k, name: deptName(k), value: v })).sort((a, b) => b.value - a.value).slice(0, 6), [d]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="glass rounded-2xl p-16 text-center text-sm text-muted-foreground">Loading dashboard…</div>;
  if (!d) return <div className="glass rounded-2xl p-16 text-center text-sm text-muted-foreground">No data yet.</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Metric icon={Boxes} tint="#0654FC" label="Total assets" value={d.total} onClick={() => navigate("/cam")} />
        <Metric icon={Wrench} tint="#e6a700" label="In repair" value={d.inRepair} sub={`${d.openRepairs} open orders`} onClick={() => navigate("/cam/maintenance")} />
        <Metric icon={PackageOpen} tint="#64748b" label="Unassigned" value={d.unassigned} onClick={() => navigate("/cam")} />
        <Metric icon={ShieldAlert} tint={d.warrantyExpiring ? "#ef4444" : "#16a34a"} label="Warranty <60d" value={d.warrantyExpiring} onClick={() => navigate("/cam/reports")} />
        <Metric icon={FileClock} tint={d.amcDue ? "#d97706" : "#16a34a"} label="AMC due" value={d.amcDue} onClick={() => navigate("/cam/amc")} />
        <Metric icon={GitPullRequestArrow} tint={d.pendingTransfers ? "#0891b2" : "#64748b"} label="Pending transfers" value={d.pendingTransfers} onClick={() => navigate("/cam/maintenance")} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ValueTile icon={IndianRupee} label="Book value" value={inr(d.totalValue)} tint="#1BA85C" />
        <ValueTile icon={Store} label="Vendors" value={d.vendors} tint="#0654FC" onClick={() => navigate("/cam/vendors")} />
        <ValueTile icon={Boxes} label="Disposed" value={d.disposed} tint="#ef4444" onClick={() => navigate("/cam/reports")} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-4">
          <div className="overline mb-3">Assets by status</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                  {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {statusData.map((s) => <span key={s.name} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"><span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.name} · {s.value}</span>)}
          </div>
        </div>

        <div className="glass rounded-2xl p-4">
          <div className="overline mb-3">Top categories</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={catData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0654FC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-3"><Building2 className="h-4 w-4 text-cobalt-500" /><div className="overline">Assets by building</div></div>
          {buildingData.length ? (
            <div className="space-y-2">
              {buildingData.map((b) => {
                const pct = Math.round((b.value / d.total) * 100);
                return (
                  <div key={b.name}>
                    <div className="flex justify-between text-sm"><span className="truncate">{b.name}</span><span className="text-muted-foreground">{b.value}</span></div>
                    <div className="h-1.5 rounded-full bg-black/5 dark:bg-white/10 mt-1"><div className="h-full rounded-full bg-cobalt-500" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-muted-foreground">No building data yet.</p>}
        </div>

        <div className="glass rounded-2xl p-4">
          <div className="overline mb-3">Owning departments</div>
          {deptData.length ? (
            <div className="space-y-2">
              {deptData.map((x) => (
                <div key={x.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                  <span className="truncate">{x.name}</span>
                  <span className="rounded-full bg-black/5 dark:bg-white/10 px-2 py-0.5 text-xs">{x.value}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No department ownership recorded yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tint, onClick }) {
  return (
    <button onClick={onClick} className="glass rounded-2xl p-3.5 text-left tap-shrink hover:shadow-md transition-shadow relative overflow-hidden group">
      <span className="absolute right-0 top-0 h-full w-1" style={{ background: tint }} />
      <span className="grid place-items-center h-8 w-8 rounded-lg mb-2" style={{ background: `${tint}1a`, color: tint }}><Icon className="h-4 w-4" /></span>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="metric text-2xl font-display leading-tight" style={{ color: tint }}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">{sub}<ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>}
    </button>
  );
}

function ValueTile({ icon: Icon, label, value, tint, onClick }) {
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} className={`glass rounded-2xl p-4 flex items-center gap-3 ${onClick ? "tap-shrink hover:shadow-md transition-shadow text-left w-full" : ""}`}>
      <span className="grid place-items-center h-10 w-10 rounded-xl shrink-0" style={{ background: `${tint}1a`, color: tint }}><Icon className="h-5 w-5" /></span>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="metric text-xl font-display leading-tight">{value}</div>
      </div>
    </Cmp>
  );
}
